import { ReadonlyVec3, vec3 } from "gl-matrix";

export type LabelKey = "top" | "bottom" | "left" | "right" | "front" | "back" | "north" | "south" | "east" | "west";

/**
 * Cap height as a fraction of the surface's in-plane height, chosen to reproduce the proportions the
 * labels had while they were still carved into the asset (5.2mm text on a 33mm face, 6.7mm compass
 * letters on a 13mm plate).
 */
const FACE_CAP_HEIGHT = 0.156;
const COMPASS_CAP_HEIGHT = 0.515;

interface LabelPlacement {
    /** Outward normal of the surface the label lands on. */
    axis: ReadonlyVec3;
    /** In-plane direction the text reads up along. */
    up: ReadonlyVec3;
    labelKey: LabelKey;
    capHeight: number;
}

/** Which letter each compass plate carries. */
const COMPASS_LABELS: Record<string, LabelKey> = {
    part_n: "north",
    part_s: "south",
    part_e: "east",
    part_w: "west",
};

/**
 * Where each localized label goes, in each mesh's own local space (+Z top, +Y back, +X right), which
 * is what the shader projects in.
 *
 * This has to be declared rather than derived. The labels used to be geometry, and the meshes that
 * remain give no hint of where text belongs: the compass plates are flat polygons whose bounds are
 * ambiguous, and the domed faces curve by as much as their own glyph height.
 */
const LABEL_PLACEMENTS: Record<string, LabelPlacement> = {
    part_left: { axis: [-1, 0, 0], up: [0, 0, 1], labelKey: "left", capHeight: FACE_CAP_HEIGHT },
    part_right: { axis: [1, 0, 0], up: [0, 0, 1], labelKey: "right", capHeight: FACE_CAP_HEIGHT },
    part_front: { axis: [0, -1, 0], up: [0, 0, 1], labelKey: "front", capHeight: FACE_CAP_HEIGHT },
    part_back: { axis: [0, 1, 0], up: [0, 0, 1], labelKey: "back", capHeight: FACE_CAP_HEIGHT },
    part_top: { axis: [0, 0, 1], up: [0, 1, 0], labelKey: "top", capHeight: FACE_CAP_HEIGHT },
    // Reads the opposite way up to the top face, so tipping the sphere over the front edge to look
    // underneath brings the label upright rather than upside down.
    part_bottom: { axis: [0, 0, -1], up: [0, -1, 0], labelKey: "bottom", capHeight: FACE_CAP_HEIGHT },
};

// Compass letters all read the same way up rather than following the ring around. Every plate is
// duplicated under the ring as `part_<x>_bottom`, modelled already facing down, so its label is
// turned over with it: like the bottom face, it reads upright once the sphere is tipped over.
for (const [name, labelKey] of Object.entries(COMPASS_LABELS)) {
    LABEL_PLACEMENTS[name] = { axis: [0, 0, 1], up: [0, 1, 0], labelKey, capHeight: COMPASS_CAP_HEIGHT };
    LABEL_PLACEMENTS[`${name}_bottom`] = {
        axis: [0, 0, -1],
        up: [0, -1, 0],
        labelKey,
        capHeight: COMPASS_CAP_HEIGHT,
    };
}

/** Fraction of the surface's in-plane width a label may occupy before it gets shrunk to fit. */
const LABEL_WIDTH_FILL = 0.92;
/**
 * Decal box height as a multiple of the cap height. The extra headroom keeps accents, descenders and
 * the taller CJK/Thai glyphs inside the box without changing the glyph size.
 */
const LABEL_HEIGHT_HEADROOM = 2;

export interface LabelDecal {
    labelKey: LabelKey;
    /** Outward surface normal, in the mesh's local space. */
    axis: vec3;
    /** In-plane axis that texture U runs along. */
    right: vec3;
    /** In-plane axis that texture V runs along. */
    up: vec3;
    /** Box center, in the mesh's local space. */
    center: vec3;
    /** Half extents along `right` and `up`. */
    halfSize: [number, number];
}

export interface MeshBounds {
    min: ReadonlyVec3;
    max: ReadonlyVec3;
}

interface NamedMesh {
    name: string;
    bounds: MeshBounds;
}

/** Extent of a bounding box along `axis`, and the midpoint of that extent. */
function projectBounds(bounds: MeshBounds, axis: ReadonlyVec3): { extent: number; center: number } {
    let min = Infinity;
    let max = -Infinity;
    const corner = vec3.create();
    for (let i = 0; i < 8; i++) {
        vec3.set(
            corner,
            i & 1 ? bounds.max[0] : bounds.min[0],
            i & 2 ? bounds.max[1] : bounds.min[1],
            i & 4 ? bounds.max[2] : bounds.min[2],
        );
        const d = vec3.dot(corner, axis);
        min = Math.min(min, d);
        max = Math.max(max, d);
    }
    return { extent: max - min, center: (min + max) / 2 };
}

/**
 * Work out a text box on every labelled surface, centered on it and scaled to its size, so the
 * shader can project the localized label onto the geometry. `rasterizeLabel` shrinks the font for
 * translations too long to fit the box.
 */
export function buildLabelDecals(meshes: readonly NamedMesh[]) {
    const decals = new Map<string, LabelDecal>();

    for (const mesh of meshes) {
        const placement = LABEL_PLACEMENTS[mesh.name];
        if (!placement) {
            continue;
        }

        const axis = vec3.normalize(vec3.create(), placement.axis as vec3);
        const right = vec3.normalize(vec3.create(), vec3.cross(vec3.create(), placement.up as vec3, axis));
        const up = vec3.normalize(vec3.create(), vec3.cross(vec3.create(), axis, right));

        const width = projectBounds(mesh.bounds, right);
        const height = projectBounds(mesh.bounds, up);
        const boxHeight = height.extent * placement.capHeight * LABEL_HEIGHT_HEADROOM;

        const center = vec3.create();
        vec3.scaleAndAdd(center, center, right, width.center);
        vec3.scaleAndAdd(center, center, up, height.center);
        vec3.scaleAndAdd(center, center, axis, projectBounds(mesh.bounds, axis).center);

        decals.set(mesh.name, {
            labelKey: placement.labelKey,
            axis,
            right,
            up,
            center,
            halfSize: [(width.extent * LABEL_WIDTH_FILL) / 2, boxHeight / 2],
        });
    }

    return decals;
}
